import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { repairsAPI } from '../../api/api';

/* =======================
   THUNKS
======================= */

export const fetchRepairs = createAsyncThunk('repairs/fetchAll', async () => {
  const { data } = await repairsAPI.getAll();
  return data.data;
});

export const fetchRepairById = createAsyncThunk(
  'repairs/fetchById',
  async (id) => {
    const { data } = await repairsAPI.getById(id);
    return data.data;
  },
);

export const fetchRepairStats = createAsyncThunk(
  'repairs/fetchStats',
  async () => {
    const { data } = await repairsAPI.getStats();
    return data.data;
  },
);

export const createRepair = createAsyncThunk(
  'repairs/create',
  async (repairData) => {
    const { data } = await repairsAPI.create(repairData);
    return data.data;
  },
);

export const updateRepair = createAsyncThunk(
  'repairs/update',
  async ({ id, data: repairData }) => {
    const { data } = await repairsAPI.update(id, repairData);
    return data.data;
  },
);

export const updateRepairStatus = createAsyncThunk(
  'repairs/updateStatus',
  // Returns the whole response body, not just `.data` -- `needsManualCompletion`
  // is a sibling field the component needs to show a heads-up dialog when
  // an invoice was drafted but deliberately left un-executed (see backend
  // sequences.md Case 2).
  async ({ id, status, notes }, { rejectWithValue }) => {
    try {
      const { data } = await repairsAPI.updateStatus(id, { status, notes });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const addRepairDevice = createAsyncThunk(
  'repairs/addDevice',
  async ({ id, data: deviceData }) => {
    await repairsAPI.addDevice(id, deviceData);
    return id;
  },
);

export const removeRepairDevice = createAsyncThunk(
  'repairs/removeDevice',
  async ({ id, deviceId }) => {
    await repairsAPI.removeDevice(id, deviceId);
    return id;
  },
);

export const addRepairAssignee = createAsyncThunk(
  'repairs/addAssignee',
  async ({ id, userId }) => {
    await repairsAPI.addAssignee(id, { user_id: userId });
    return id;
  },
);

export const removeRepairAssignee = createAsyncThunk(
  'repairs/removeAssignee',
  async ({ id, userId }) => {
    await repairsAPI.removeAssignee(id, userId);
    return id;
  },
);

export const addRepairPart = createAsyncThunk(
  'repairs/addPart',
  async ({ id, data: partData }, { rejectWithValue }) => {
    try {
      await repairsAPI.addPart(id, partData);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const removeRepairPart = createAsyncThunk(
  'repairs/removePart',
  async ({ id, partsUsedId }) => {
    await repairsAPI.removePart(id, partsUsedId);
    return id;
  },
);

export const deleteRepair = createAsyncThunk(
  'repairs/delete',
  async (id, { rejectWithValue }) => {
    try {
      await repairsAPI.delete(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

/* =======================
   SLICE
======================= */

const repairsSlice = createSlice({
  name: 'repairs',
  initialState: {
    items: [],
    currentRepair: null,
    stats: null,
    // List and single-ticket fetches keep separate flags: sharing one meant a
    // ticket request finishing after navigating back to the list flipped
    // `loading` off mid-fetch, so the board rendered empty (all 0) until a
    // manual page refresh.
    loading: false,
    error: null,
    listRequestId: null,
    detailLoading: false,
    detailError: null,
  },
  reducers: {
    clearCurrentRepair: (state) => {
      state.currentRepair = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* FETCH ALL */
      // Only the latest list request may settle the state, so an older,
      // slower response can't overwrite fresher data.
      .addCase(fetchRepairs.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(fetchRepairs.fulfilled, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchRepairs.rejected, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.error = action.error.message;
      })

      /* FETCH BY ID */
      .addCase(fetchRepairById.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchRepairById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.currentRepair = action.payload;
      })
      .addCase(fetchRepairById.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.error.message;
      })

      /* STATS */
      .addCase(fetchRepairStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })

      /* CREATE */
      .addCase(createRepair.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })

      /* UPDATE */
      .addCase(updateRepair.fulfilled, (state, action) => {
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) state.items[index] = action.payload;
        if (state.currentRepair?.id === action.payload.id) {
          state.currentRepair = { ...state.currentRepair, ...action.payload };
        }
      })

      /* UPDATE STATUS */
      .addCase(updateRepairStatus.fulfilled, (state, action) => {
        const updatedTicket = action.payload.data;
        const repair = state.items.find((r) => r.id === updatedTicket.id);
        if (repair) repair.status = updatedTicket.status;
        if (state.currentRepair?.id === updatedTicket.id) {
          state.currentRepair.status = updatedTicket.status;
        }
      })

      /* DELETE */
      .addCase(deleteRepair.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.id !== action.payload);
      });
  },
});

export const { clearCurrentRepair, clearError } = repairsSlice.actions;
export default repairsSlice.reducer;
